import { useEffect, useRef } from "react";
import { useT } from "@/i18n";
import jsVectorMap from "jsvectormap";
import "jsvectormap/dist/jsvectormap.css";
import "@/maps/id";
import AnimatedCounter from "@/components/AnimatedCounter";

export default function Mission() {
  const ref = useRef(null);
  const mapEl = useRef(null);
  const mapInstance = useRef(null);
  const { t, lang } = useT();

  useEffect(() => {
    if (!mapEl.current || mapInstance.current) return;
    // Guard against StrictMode double-invocation leaving a stale SVG
    if (mapEl.current.querySelector("svg")) {
      mapEl.current.innerHTML = "";
    }
    try {
      mapInstance.current = new jsVectorMap({
        selector: mapEl.current,
        map: "id",
        backgroundColor: "transparent",
        zoomOnScroll: false,
        zoomButtons: false,
        draggable: false,
        regionStyle: {
          initial: {
            fill: "rgba(12,14,20,0.06)",
            stroke: "rgba(12,14,20,0.10)",
            strokeWidth: 0.5,
          },
          hover: { fill: "rgba(91,75,255,0.30)" },
          selected: { fill: "#5b4bff" },
        },
        regionsSelectable: false,
        // Highlight provinces hosting our operational + partner nodes across Indonesia
        selectedRegions: [
          "ID_jk", "ID_jb", "ID_bt", "ID_jt", "ID_yo", "ID_jl",
          "ID_ba", "ID_su", "ID_sn", "ID_sa", "ID_ki", "ID_kt",
        ],
      });
    } catch (e) { console.warn("jsVectorMap init failed:", e); }

    return () => {
      try { mapInstance.current?.destroy?.(); } catch (e) { /* ignore */ }
      // jsvectormap.destroy() does NOT remove its rendered <svg> from the container.
      // Clear it so React.StrictMode's 2nd effect invocation doesn't stack maps.
      if (mapEl.current) mapEl.current.innerHTML = "";
      mapInstance.current = null;
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="mission-root"
      className="relative w-full h-screen overflow-hidden flex flex-col"
      style={{ background: "var(--paper-2)" }}
    >
      {/* faint grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: "linear-gradient(90deg, transparent calc(100% - 1px), #0c0e14 calc(100% - 1px))",
          backgroundSize: "12.5% 100%",
        }}
      />

      <div className="relative z-10 w-full px-6 sm:px-8 md:px-16 lg:px-24 pt-36 md:pt-32 pb-10 flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-black/10 pb-5 mb-8 flex-none">
          <div className="flex items-baseline gap-4 md:gap-6">
            <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.32em] text-black/40">
              {lang === "id" ? "Jangkauan" : "Reach"} · 03
            </span>
            <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.32em] text-black/40 hidden md:inline">
              {lang === "id" ? "Atlas Operasional" : "Operational Atlas"}
            </span>
          </div>
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.28em] text-black/40">
            /03 · /09
          </span>
        </div>

        {/* Headline + map split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 flex-1 min-h-0 overflow-hidden items-center">
          {/* Headline — LEFT */}
          <div className="lg:col-span-5 flex flex-col">
            <h2
              className="font-display text-[var(--ink)]"
              style={{
                fontSize: "clamp(1.85rem, 3.8vw, 3.4rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.025em",
                fontWeight: 600,
              }}
            >
              {t("reach.title")}
            </h2>
            <p className="mt-7 md:mt-8 text-black/55 text-sm md:text-[15px] leading-relaxed max-w-[28rem] border-l border-black/15 pl-5">
              {lang === "id"
                ? "Jaringan profesional lintas pulau dengan ritme delivery dan standar mutu yang sama. Tersebar di Sumatra, Jawa, Bali, Kalimantan, hingga Sulawesi."
                : "A cross-island professional network shipping at the same rhythm and quality bar — spanning Sumatra, Java, Bali, Kalimantan, through to Sulawesi."}
            </p>

            {/* Animated stat counters */}
            <dl
              data-testid="mission-stats"
              className="mt-8 md:mt-10 grid grid-cols-3 gap-4 md:gap-6 max-w-[30rem]"
            >
              <div className="border-t border-black/15 pt-3">
                <dt className="font-mono text-[0.55rem] uppercase tracking-[0.28em] text-black/45 mb-1">
                  {lang === "id" ? "Provinsi" : "Provinces"}
                </dt>
                <dd
                  className="font-display text-[var(--ink)]"
                  style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", letterSpacing: "-0.02em", fontWeight: 700, lineHeight: 1 }}
                >
                  <AnimatedCounter value={12} data-testid="stat-provinces" />
                </dd>
              </div>
              <div className="border-t border-black/15 pt-3">
                <dt className="font-mono text-[0.55rem] uppercase tracking-[0.28em] text-black/45 mb-1">
                  {lang === "id" ? "Klien" : "Clients"}
                </dt>
                <dd
                  className="font-display text-[var(--ink)]"
                  style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", letterSpacing: "-0.02em", fontWeight: 700, lineHeight: 1 }}
                >
                  <AnimatedCounter value={120} suffix="+" data-testid="stat-clients" />
                </dd>
              </div>
              <div className="border-t border-black/15 pt-3">
                <dt className="font-mono text-[0.55rem] uppercase tracking-[0.28em] text-black/45 mb-1">
                  {lang === "id" ? "Proyek" : "Projects"}
                </dt>
                <dd
                  className="font-display text-[var(--ink)]"
                  style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", letterSpacing: "-0.02em", fontWeight: 700, lineHeight: 1 }}
                >
                  <AnimatedCounter value={380} suffix="+" data-testid="stat-projects" />
                </dd>
              </div>
            </dl>
          </div>

          {/* Map — RIGHT, constrained */}
          <div className="lg:col-span-7 relative min-h-0 w-full">
            <div className="relative w-full h-full flex items-center justify-center">
              <div
                className="relative w-full"
                style={{ maxHeight: "100%", aspectRatio: "16 / 9" }}
              >
                {/* faint corner brackets */}
                <span aria-hidden className="absolute top-0 left-0 w-6 h-6 border-l border-t border-black/25" />
                <span aria-hidden className="absolute top-0 right-0 w-6 h-6 border-r border-t border-black/25" />
                <span aria-hidden className="absolute bottom-0 left-0 w-6 h-6 border-l border-b border-black/25" />
                <span aria-hidden className="absolute bottom-0 right-0 w-6 h-6 border-r border-b border-black/25" />

                <div ref={mapEl} className="absolute inset-0 px-3 py-3 mission-map" data-testid="mission-map" />

                {/* corner labels */}
                <span className="absolute top-2 left-3 font-mono text-[0.55rem] tracking-[0.24em] uppercase text-black/40">
                  ATLAS · ID
                </span>
                <span className="absolute bottom-2 right-3 font-mono text-[0.55rem] tracking-[0.24em] uppercase text-black/40">
                  v1.0
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom rule */}
        <div className="mt-6 flex items-center justify-between font-mono text-[0.55rem] md:text-[0.6rem] uppercase tracking-[0.28em] text-black/40 flex-none">
          <span>{lang === "id" ? "Operasional · Lintas pulau" : "Operations · Cross-island"}</span>
          <span>12 NODES</span>
        </div>
      </div>

      {/* Region highlight pulse glow via CSS on selected provinces */}
      <style>{`
        .mission-map svg path[fill="#5b4bff"] {
          filter: drop-shadow(0 0 8px rgba(91,75,255,0.55));
          animation: missionPulse 3s ease-in-out infinite;
        }
        @keyframes missionPulse {
          0%, 100% { filter: drop-shadow(0 0 6px rgba(91,75,255,0.40)); }
          50%      { filter: drop-shadow(0 0 16px rgba(91,75,255,0.85)); }
        }
      `}</style>
    </div>
  );
}
